import { useAuth } from '@/features/authentication';
import { converseApi } from '@/features/messaging';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { BlockMemberButton } from '@/widgets/member-block';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function ChatDetailPage() {
  const { converseId = '' } = useParams<{ converseId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: converse, isLoading: isConverseLoading } = useQuery({
    queryKey: ['converse', converseId],
    queryFn: () => converseApi.getConverse(Number.parseInt(converseId)),
  });

  const { data: messagesData, isLoading: isMessagesLoading } = useQuery({
    queryKey: ['messages', converseId],
    queryFn: () => converseApi.getMessages(Number.parseInt(converseId), 1, 100),
    refetchInterval: 5000,
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => converseApi.sendMessage(Number.parseInt(converseId), content),
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['messages', converseId] });
    },
    onError: () => {
      toast.error("Échec de l'envoi du message");
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesData]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    sendMutation.mutate(message.trim());
  };

  const formatMessageDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    }
    if (isYesterday(date)) {
      return `Hier ${format(date, 'HH:mm')}`;
    }
    return format(date, 'd MMM, HH:mm', { locale: fr });
  };

  const isLoading = isConverseLoading || isMessagesLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(100vh-12rem)]">
        <Card className="mb-4">
          <div className="flex items-center gap-4 p-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        </Card>
        <div className="flex-1 py-4 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
              <Skeleton
                className={`h-16 w-56 rounded-2xl ${i % 2 === 0 ? 'rounded-bl-md' : 'rounded-br-md'}`}
              />
            </div>
          ))}
        </div>
        <Card className="p-4">
          <Skeleton className="h-12 w-full" />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      {/* Header */}
      <Card className="mb-4">
        <div className="flex items-center gap-4 p-4">
          <Button variant="ghost" size="icon" asChild className="hover:bg-primary-surface">
            <Link to="/chat">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <Link
            to={`/profile/${converse?.participantNodeId}`}
            className="flex items-center gap-3 flex-1 group"
          >
            <div className="relative">
              <Avatar className="h-12 w-12 ring-2 ring-border group-hover:ring-primary/30 transition-all duration-300">
                <AvatarImage src={converse?.participantAvatar} alt={converse?.participantName} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {converse?.participantName?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              {converse?.participantOnline && (
                <div className="absolute bottom-0 right-0 h-4 w-4 bg-success rounded-full border-2 border-card" />
              )}
            </div>
            <div>
              <h2 className="font-semibold group-hover:text-primary transition-colors">
                {converse?.participantName}
              </h2>
              {converse?.participantOnline ? (
                <span className="text-xs text-success font-medium">En ligne</span>
              ) : (
                <span className="text-xs text-muted-foreground">Hors ligne</span>
              )}
            </div>
          </Link>
          {converse?.participantNodeId ? (
            <BlockMemberButton
              compact
              nodeId={converse.participantNodeId}
              username={converse.participantName ?? ''}
              onBlocked={() => navigate('/chat')}
            />
          ) : null}
        </div>
      </Card>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4 px-2">
        {messagesData?.items && messagesData.items.length > 0 ? (
          [...messagesData.items].reverse().map((msg) => {
            const isOwn = msg.senderNodeId === user?.nodeId;
            return (
              <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                    isOwn
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-card border border-border rounded-bl-md'
                  }`}
                >
                  <p className="break-words leading-relaxed">{msg.content}</p>
                  <p
                    className={`text-xs mt-1.5 ${
                      isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}
                  >
                    {formatMessageDate(msg.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <div className="h-16 w-16 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-4">
              <Send className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-display text-lg font-semibold mb-2">Aucun message</h3>
            <p className="text-muted-foreground">Commencez la conversation !</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <Card className="mt-4">
        <form onSubmit={handleSend} className="p-4">
          <div className="flex gap-3">
            <Input
              placeholder="Écrivez votre message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sendMutation.isPending}
              autoFocus
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={sendMutation.isPending || !message.trim()}
              className="px-6"
            >
              {sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
